import { CountryCode, CountryName } from '@dayetopia/types';

export const countriesInEULocale = [
  CountryCode.NL,
  CountryCode.BE,
  CountryCode.IT,
  CountryCode.ES,
  CountryCode.PT,
  CountryCode.FR,
  CountryCode.SE,
  CountryCode.DK,
  CountryCode.NO,
  CountryCode.FI,
  CountryCode.AT,
  CountryCode.CH
];

// for e.g. -US would show in all apart from US, and US,GB would show only in both US & GB, works with , separators too
export function showInCountryCondition(showInCountries?: string, country?: CountryCode | string): boolean {
  if (!country || !showInCountries?.length) {
    return true;
  }

  const showInCountriesArray = showInCountries
    ?.split(',')
    .map((c) => c.trim())
    .filter((c) => !!c);

  if (!showInCountriesArray?.length) {
    return true;
  }

  let include = false;
  let exclude = false;

  showInCountriesArray.forEach((showInCountrySingle) => {
    if (showInCountrySingle.startsWith('-')) {
      if (showInCountrySingle.slice(1) === country) {
        exclude = true;
      }
    } else if (showInCountrySingle === country) {
      include = true;
    }
  });

  if (exclude) {
    return false;
  }

  return include || showInCountriesArray.every((c) => c.startsWith('-'));
}

/**
 * Filters an array of items based on the specified country.
 *
 * @template T - The type of the items.
 * @param {Array<T>} menuItems - The array of items to filter.
 * @param {CountryCode | string | undefined} country - The country code or name to filter by.
 * @returns {Array<T>} The filtered array of items that match the country condition.
 */
export function filterShowInCountries<T>(menuItems: Array<T>, country: CountryCode | string | undefined): Array<T> {
  return (
    menuItems?.filter((item: any) => {
      const showInCountries = item?.show_in_countries?.text || item?.show_in_countries;

      return showInCountryCondition(showInCountries, country);
    }) || []
  );
}

/**
 * We have a record of countries that are no longer in the EU locale,
 * have their respective locales,
 * but still have services available in English.
 *
 */
export const countriesNoLongerInEULocale = [CountryCode.IE, CountryCode.DE, CountryCode.BG];

export const isCountryInEULocale = (country: CountryCode) => {
  return countriesInEULocale.includes(country);
};

export const isCountryIsNoLongerInEULocale = (country: CountryCode) => {
  return countriesNoLongerInEULocale.includes(country);
};

/**
 * Gets the CountryCode for a given country name
 * @param countryName The full name of the country (e.g. "United Kingdom")
 * @returns The corresponding CountryCode (e.g. "GB") or undefined if not found
 */
export function getCountryCodeByName(countryName: string): CountryCode | undefined {
  const countryNameToCode = Object.entries(CountryName).reduce((acc, [code, name]) => {
    acc[name] = code as CountryCode;
    return acc;
  }, {} as Record<string, CountryCode>);

  return countryNameToCode[countryName];
}
